import { useQuery } from "@tanstack/react-query";
import { fetchQuestionBankQuestions } from "../services/questionBankQuestionService";

function extractPagination(payload = {}) {
  const pagination = payload?.pagination || {};

  return {
    currentPage: Number(pagination?.currentPage || pagination?.current_page || 1),
    perPage: Number(pagination?.perPage || pagination?.per_page || 10),
    totalRecords: Number(pagination?.totalRecords || pagination?.total_records || 0),
    totalPages: Number(pagination?.totalPages || pagination?.total_pages || 1),
    nextPage: Number(pagination?.nextPage || pagination?.next_page || 0),
    previousPage: Number(pagination?.previousPage || pagination?.previous_page || pagination?.prevPage || pagination?.prev_page || 0),
  };
}

function getLocalizedText(item = {}, baseField) {
  return (
    item?.[`${baseField}_en`] ||
    item?.[baseField] ||
    item?.[`${baseField}_hi`] ||
    ""
  );
}

function normalizeOption(option = {}, index = 0) {
  return {
    id: Number(option?.option_id || option?.id || index + 1),
    label: getLocalizedText(option, "option_name") || option?.title || `Option ${index + 1}`,
    isCorrect: String(option?.is_option_correct || "").toUpperCase() === "Y",
    isSelected: Number(option?.user_selected_option || 0) === 1,
  };
}

function normalizeQuestion(item = {}, index = 0, page = 1, perPage = 0) {
  const questionIndex = Number(
    item?.question_index || item?.question_number || item?.serial_no || item?.sr_no || (Math.max(1, page) - 1) * perPage + index + 1
  );

  return {
    id: item?.id || item?.question_id || item?.qb_question_id || index + 1,
    serialNo: questionIndex,
    question_index: questionIndex,
    question_number: questionIndex,
    question:
      item?.question ||
      item?.question_html ||
      item?.question_text ||
      item?.question_title ||
      item?.question_name ||
      item?.name ||
      item?.title ||
      "Question will be available soon.",
    questionHtml:
      item?.question ||
      item?.question_html ||
      item?.question_text ||
      item?.question_title ||
      item?.question_name ||
      item?.name ||
      item?.title ||
      "Question will be available soon.",
    questionType: String(item?.question_type || ""),
    questionImage: item?.question_image || "",
    questionStatementOne: getLocalizedText(item, "question_statement_one"),
    questionStatementTwo: getLocalizedText(item, "question_statement_two"),
    options: Array.isArray(item?.question_option) ? item.question_option.map(normalizeOption) : [],
    rawOptions: Array.isArray(item?.question_option) ? item.question_option : [],
    paymentStatus: Number(item?.payment_status ?? 0),
    isLocked:
      (String(item?.free_paid || "").toUpperCase() !== "F" && Number(item?.payment_status) !== 1 && Number(item?.is_locked ?? 0) === 1) ||
      (String(item?.free_paid || "").toUpperCase() === "P" && Number(item?.payment_status ?? 0) !== 1),
    isBookmarked:
      Number(item?.bookmark_status ?? item?.is_bookmarked ?? item?.is_favourite ?? 0) === 1,
    isLiked:
      Number(item?.like_status ?? item?.is_liked ?? 0) === 1,
    reportStatus:
      Number(item?.report_status ?? item?.is_reported ?? 0) === 1,
    likeCount: Number(item?.total_likes ?? item?.like_count ?? item?.likes_count ?? item?.likes ?? 0),
    viewCount: Number(item?.total_views ?? item?.view_count ?? item?.views_count ?? item?.views ?? 0),
    reportCount:
      item?.total_reports ??
      item?.report_count ??
      item?.reports_count ??
      "0",
    notesCount: Number(item?.total_notes ?? item?.notes_count ?? item?.personal_notes_count ?? item?.notes ?? 0),
    isGuess:
      Number(item?.is_guess_answer ?? item?.guess_status ?? item?.is_guess ?? 0) === 1,
    isAnswered: Number(item?.is_question_answered ?? item?.question_answered ?? 0) === 1,
    isGuessCorrect:
      Number(item?.guess_correct ?? item?.is_guess_correct ?? item?.right_guess ?? 0) === 1,
    isGuessWrong:
      Number(item?.guess_wrong ?? item?.is_guess_wrong ?? item?.wrong_guess ?? 0) === 1,
    selectedOptionId:
      Number(
        item?.selected_option_id ||
          item?.user_selected_option_id ||
          item?.question_option?.find((option) => Number(option?.user_selected_option || 0) === 1)?.option_id ||
          0
      ) || null,
    questionTime: Number(item?.question_time || 0),
    free_paid: item?.free_paid || "",
    isPaidBlocked:
      String(item?.free_paid || "").toUpperCase() === "P" && Number(item?.payment_status ?? 0) !== 1,
    isPyq: Number(item?.is_pyq || 0) === 1,
    pyqMappings: Array.isArray(item?.pyq_mappings) ? item.pyq_mappings : [],
    raw: item,
  };
}

export function useQuestionBankQuestions(params = {}, options = {}) {
  return useQuery({
    queryKey: ["questionBankQuestions", params],
    queryFn: async () => {
      const { payload } = await fetchQuestionBankQuestions(params);
      const rawQuestions =
        payload?.question_list ||
        payload?.questions_list ||
        payload?.questions ||
        payload?.data ||
        [];
      const pagination = extractPagination(payload);
      const normalizedQuestions = (Array.isArray(rawQuestions) ? rawQuestions : []).map((item, index) =>
        normalizeQuestion(item, index, pagination.currentPage, pagination.perPage)
      );
      const guessedQuestions = Number(
        payload?.guess_questions ??
          payload?.total_guess_questions ??
          payload?.guess_question_count ??
          normalizedQuestions.filter((item) => item.isGuess).length ??
          0
      );
      const rightGuess = Number(
        payload?.right_guess ??
          payload?.right_guess_questions ??
          payload?.correct_guess_questions ??
          normalizedQuestions.filter((item) => item.isGuessCorrect).length ??
          0
      );
      const wrongGuess = Number(
        payload?.wrong_guess ??
          payload?.wrong_guess_questions ??
          payload?.incorrect_guess_questions ??
          normalizedQuestions.filter((item) => item.isGuessWrong).length ??
          0
      );
      const guessScore =
        payload?.guess_score_percent ||
        payload?.guess_score ||
        payload?.guessScore ||
        "0%";

      return {
        stateName: payload?.state_name || "",
        subjectName: payload?.subject_name || "",
        categoryName: payload?.category_name || "",
        guessScore,
        guessSummary: {
          score: guessScore,
          guessQuestions: guessedQuestions,
          rightGuess,
          wrongGuess,
        },
        totalResults:
          Number(payload?.total_results || payload?.question_count || extractPagination(payload).totalRecords || 0),
        questions: normalizedQuestions,
        pagination: extractPagination(payload),
      };
    },
    enabled: options.enabled ?? true,
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    retry: 2,
    refetchOnMount: "always",
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) =>
      previousData ?? {
        stateName: "",
        subjectName: "",
        categoryName: "",
        guessScore: "0%",
        guessSummary: {
          score: "0%",
          guessQuestions: 0,
          rightGuess: 0,
          wrongGuess: 0,
        },
        totalResults: 0,
        questions: [],
        pagination: {
          currentPage: 1,
          perPage: 0,
          totalRecords: 0,
          totalPages: 1,
          nextPage: 0,
          previousPage: 0,
        },
      },
  });
}
